import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useParserMetrics } from '@/hooks/useParserMetrics';
import { CheckCircle2, AlertTriangle, XCircle, Clock, TrendingUp, Download, Calendar } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ParserMetricsCharts } from './ParserMetricsCharts';

export function ParserMetricsDashboard() {
  const { metrics, isLoading } = useParserMetrics();
  const [dateRange, setDateRange] = useState('today');

  const exportToCSV = () => {
    if (!metrics) return;

    const csvData = [
      ['Metric', 'Value'],
      ['Parsed Today', metrics.today_parsed],
      ['Avg Confidence', (metrics.today_avg_confidence * 100).toFixed(1) + '%'],
      ['Needs Review', metrics.needs_review],
      ['Failed', metrics.failed],
      ['Avg Processing Time', (metrics.avg_processing_time_ms / 1000).toFixed(1) + 's'],
      ['Success Rate', ((metrics.today_parsed / (metrics.today_parsed + metrics.failed)) * 100).toFixed(1) + '%'],
      [''],
      ['Vendor', 'Bills', 'Avg Confidence', 'Success Rate', 'Avg Time'],
      ...metrics.vendor_stats.map(v => [
        v.vendor,
        v.parsed_count,
        (v.avg_confidence * 100).toFixed(1) + '%',
        (v.success_rate * 100).toFixed(1) + '%',
        (v.avg_time_ms / 1000).toFixed(1) + 's'
      ])
    ];

    const csv = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `parser-metrics-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6">
              <div className="h-20 animate-pulse bg-muted rounded" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!metrics) return null;

  const successRate = metrics.today_parsed > 0
    ? ((metrics.today_parsed / (metrics.today_parsed + metrics.failed)) * 100).toFixed(1)
    : '0.0';

  return (
    <div className="space-y-4 mb-6">
      {/* Filters and Export */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <Select value={dateRange} onValueChange={setDateRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="7days">Last 7 Days</SelectItem>
              <SelectItem value="30days">Last 30 Days</SelectItem>
              <SelectItem value="custom">Custom Range</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" size="sm" onClick={exportToCSV}>
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Key Metrics */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Parsed Today</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{metrics.today_parsed}</p>
                  <Badge variant="default" className="gap-1">
                    <TrendingUp className="h-3 w-3" />
                    {(metrics.today_avg_confidence * 100).toFixed(0)}%
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Avg confidence: {(metrics.today_avg_confidence * 100).toFixed(1)}%
                </p>
              </div>
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Needs Review</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{metrics.needs_review}</p>
                  <Badge variant="secondary" className="gap-1">
                    {(metrics.needs_review_avg_confidence * 100).toFixed(0)}%
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground">
                  Below confidence threshold
                </p>
              </div>
              <AlertTriangle className="h-8 w-8 text-yellow-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Failed</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">{metrics.failed}</p>
                  {metrics.failed > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Will retry in 1h
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Success rate: {successRate}%
                </p>
              </div>
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">Avg Processing</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-2xl font-bold">
                    {(metrics.avg_processing_time_ms / 1000).toFixed(1)}s
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">
                  Processing speed
                </p>
              </div>
              <Clock className="h-8 w-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <ParserMetricsCharts />

      {/* Vendor Performance */}
      {metrics.vendor_stats.length > 0 && (
        <Card>
          <CardContent className="p-6">
            <h3 className="font-semibold mb-4">Performance by Vendor</h3>
            <div className="space-y-3">
              {metrics.vendor_stats.map((vendor) => (
                <div key={vendor.vendor} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <div className="font-medium">{vendor.vendor}</div>
                    <div className="flex items-center gap-4 text-muted-foreground">
                      <span>{vendor.parsed_count} bills</span>
                      <span>{(vendor.avg_confidence * 100).toFixed(0)}% conf</span>
                      <span>{(vendor.avg_time_ms / 1000).toFixed(1)}s</span>
                    </div>
                  </div>
                  <Progress value={vendor.success_rate * 100} className="h-1" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
